import { createSlice } from '@reduxjs/toolkit';

import { cloneDeep } from '@disy/lib/clone-deep';
import { isEqual } from '@disy/lib/is-equal';
import { cadenzaSessionStorage } from '@disy/lib/storage';

import { uniqueTimelineId } from 'cadenza/api-client/base-timeline-api';
import type { TimelineLayerFadingDurationUnit, TimelinesDto } from 'cadenza/api-client/timeline-dto';
import { TimelineLayerFadingTransition, TimelineLayerFadingType } from 'cadenza/api-client/timeline-dto';
import { type RootStateOr, addMiddleware, createSliceFunction, getStore } from 'cadenza/store/cadenza-store';
import { createObserveMiddleware, observeStore } from 'cadenza/store/state-utils';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getDataViewIdsWithTimelineData } from 'cadenza/workbook/state/workbook-timeline';
import { getInvalidViewsOrDataViews } from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

const SLICE_PREFIX = 'timelineSlice';

declare module 'cadenza/store/cadenza-store' {
  interface RootState {
    [SLICE_PREFIX]: TimelinePlayerState;
  }
}

const slice = createSliceFunction(SLICE_PREFIX);

const TIMELINE_SETTINGS_KEY = 'timeline-settings';

export interface TimelinePlayerSettings {
  /**  True if the custom settings should be applied */
  customSettingsApplied: true;
  /** Whether and how points should be faded out */
  fading: TimelinePlayerFadingSettings;
  /** Whether the last (current) point should be highlighted on the map */
  highlightLastPoint: boolean;

  tracking: TrackingSettings;

  displayData: DisplayDataSettings;

  /**
   * The short-click action of the fast-forward button jumps to the next earliest point in time that has minimum distance X
   */
  skipXMeters: number;
}

export interface TimelinePlayerFadingSettings {
  transition: TimelineLayerFadingTransition;
  type: TimelineLayerFadingType;
  count?: number;
  duration?: {
    milliseconds: number;
    configurationUnit: TimelineLayerFadingDurationUnit;
  };
}

export interface TrackingSettings {
  trackingEnabled: boolean;
  trackingEnabledByTimeline: Record<string, boolean>;
}

interface DisplayDataSettings {
  displayDataOnCurrentPoint: boolean;
  includeData: DisplayablePointData[];
}

export type DisplayablePointData = 'SPEED' | 'SATELLITE_COUNT' | 'ADDRESS';

const DEFAULT_SETTINGS: TimelinePlayerSettings = {
  customSettingsApplied: true,
  fading: {
    transition: TimelineLayerFadingTransition.TRANSPARENCY,
    type: TimelineLayerFadingType.AFTER_COUNT_FADING,
    count: 20,
  },
  highlightLastPoint: true,
  tracking: {
    trackingEnabled: true,
    trackingEnabledByTimeline: {},
  },
  displayData: {
    displayDataOnCurrentPoint: true,
    includeData: ['SPEED', 'SATELLITE_COUNT'],
  },
  skipXMeters: 1,
};

export type PlayingState = 'PAUSED' | 'PLAYING' | 'PLAYING_BACKWARDS' | 'FAST_FORWARDING' | 'FAST_REVERSING';

interface TimelinePlayerState {
  /**
   * True if timeline player should be displayed
   */
  open: boolean;

  /**
   * Id of the map view the timeline player is playing for.
   * Available only when on workbooks and timeline player is open.
   */
  mapViewId?: ViewId;

  /**
   * Coordinates to display all in map
   */
  mapViewCoordinates?: number[][];

  /**
   * Last time the mapViewCoordinated have been re-calculated as milliseconds since epoch
   */
  lastMapViewCoordinatesUpdate?: number;

  /**
   * The current time the timeline player is set to.
   */
  time: number;

  /**
   * The timelines to display
   */
  timelines?: TimelinesDto;

  /**
   * True, if we need to update timelines, possibly already are updating
   */
  updatingTimelines: boolean;

  /**
   * True if maximum number of points to display exceeded.
   */
  maximumDisplayPointCountExceeded: boolean;

  /**
   * Custom settings
   */
  customSettings: TimelinePlayerSettings;

  playingState: {
    current: PlayingState;
    previous: PlayingState;
  };
}

/**
 * When timelines are changed, update the tracking settings by timeline:
 * For timeline that were already present, keep the current setting, and for new ones, set to true.
 *
 * @param state
 */
function updateTrackingSettingsByTimeline(state: TimelinePlayerState) {
  const trackingEnabledByTimeline = state.customSettings.tracking.trackingEnabledByTimeline;
  const newTrackingEnabledByTimeline: Record<string, boolean> = {};
  state.timelines?.timelines.forEach((timeline) => {
    const uniqueId = uniqueTimelineId(timeline);
    newTrackingEnabledByTimeline[uniqueId] = trackingEnabledByTimeline[uniqueId] ?? true;
  });
  state.customSettings.tracking.trackingEnabledByTimeline = newTrackingEnabledByTimeline;
}

function getInitialCustomSettings() {
  let previousSettings = cadenzaSessionStorage.getItem(TIMELINE_SETTINGS_KEY);
  return previousSettings ? JSON.parse(previousSettings) : DEFAULT_SETTINGS;
}

const initialState: TimelinePlayerState = {
  open: false,
  mapViewId: undefined,
  mapViewCoordinates: undefined,
  lastMapViewCoordinatesUpdate: undefined,
  time: 0,
  timelines: undefined,
  updatingTimelines: false,
  maximumDisplayPointCountExceeded: false,
  customSettings: getInitialCustomSettings(),
  playingState: {
    previous: 'PAUSED',
    current: 'PAUSED',
  },
};

const timelineSlice = createSlice({
  name: SLICE_PREFIX,
  initialState: cloneDeep(initialState),
  reducers: {
    openTimelinePlayer(state: TimelinePlayerState, { payload: mapViewId }: { payload: ViewId | undefined }) {
      // In case the timeline player is already running for another view, we need to pause it because
      // it can occur that we need user confirmation if timeline has more points than configured warning
      // or the new timeline exceeds maximum allowed number of points - in which user will see info
      // dialog, but timeline shouldn't be playing in the background
      if (isTimelinePlayerOpen(state)) {
        transitionToPlayingStateInternal(state, 'PAUSED');
      }

      const shouldOpenAndLoadTimelines = state.mapViewId !== mapViewId || !state.open;
      state.mapViewId = mapViewId;
      state.updatingTimelines = shouldOpenAndLoadTimelines;
      state.open = shouldOpenAndLoadTimelines;
    },
    closeTimelinePlayer(state: TimelinePlayerState) {
      // Reset state, except for the custom settings
      Object.assign(state, initialState, { customSettings: state.customSettings });
    },
    setTimelinePlayerTime(state: TimelinePlayerState, { payload: time }) {
      state.time = time;
    },
    incrementTimelinePlayerTime(state: TimelinePlayerState, { payload: incrementMs }) {
      state.time += incrementMs;
    },
    setTimelinePlayerTimelines(
      state: TimelinePlayerState,
      { payload: timelines }: { payload: TimelinesDto | undefined },
    ) {
      state.timelines = timelines;
      state.updatingTimelines = false;
      updateTrackingSettingsByTimeline(state);
    },
    setMaximumDisplayPointCountExceeded(state: TimelinePlayerState, { payload: exceeded }) {
      state.maximumDisplayPointCountExceeded = exceeded;
    },
    setCustomSettingsApplied(state: TimelinePlayerState, { payload: customSettingsApplied }) {
      state.customSettings.customSettingsApplied = customSettingsApplied;
    },
    setFadingSettings(state: TimelinePlayerState, { payload: customFadingSettings }) {
      state.customSettings.fading = customFadingSettings;
    },
    setHighlightLastPointSettings(state: TimelinePlayerState, { payload: highlightLastPoint }) {
      state.customSettings.highlightLastPoint = highlightLastPoint;
    },
    setMapTrackingSettings(state: TimelinePlayerState, { payload: trackingSettings }) {
      state.customSettings.tracking = trackingSettings;
    },
    toggleMapTrackingEnabled(state: TimelinePlayerState) {
      state.customSettings.tracking.trackingEnabled = !state.customSettings.tracking.trackingEnabled;
    },
    setTimelinePlayerMapViewCoordinates(state: TimelinePlayerState, { payload: coordinates }) {
      state.mapViewCoordinates = coordinates;
      state.lastMapViewCoordinatesUpdate = Date.now();
    },
    toggleDisplayDataOnCurrentPoint(state: TimelinePlayerState) {
      state.customSettings.displayData.displayDataOnCurrentPoint =
        !state.customSettings.displayData.displayDataOnCurrentPoint;
    },
    setSkipXMeters(state: TimelinePlayerState, { payload: skipXMeters }) {
      state.customSettings.skipXMeters = skipXMeters;
    },
    toggleDisplayablePointData(state: TimelinePlayerState, { payload: { displayablePointData } }) {
      const includeData = state.customSettings.displayData.includeData;
      state.customSettings.displayData.includeData =
        includeData.includes(displayablePointData) ?
          includeData.filter((it) => it !== displayablePointData)
        : [...includeData, displayablePointData];
    },
    transitionToPlayingState(state: TimelinePlayerState, { payload: newPlayingState }) {
      transitionToPlayingStateInternal(state, newPlayingState);
    },
    transitionToPreviousPlayingState(state: TimelinePlayerState) {
      const previousState = state.playingState.previous;
      state.playingState.previous = state.playingState.current;
      state.playingState.current = previousState;
    },
    updateTimelinePlayerTimelines(state: TimelinePlayerState) {
      if (state.open) {
        state.timelines = undefined;
        state.updatingTimelines = true;
      }
    },
  },
});

const { reducer, actions } = timelineSlice;
getStore().registerReducer(SLICE_PREFIX, reducer);

// Action creators
export const {
  /**
   * This setter should be used by TimelinePlayerPostMessageApi only.
   * It is used for syncing the state between frames.
   *
   * @param state - The new TimelineState
   */
  openTimelinePlayer,
  closeTimelinePlayer,
  setTimelinePlayerTime,
  incrementTimelinePlayerTime,
  setTimelinePlayerTimelines,
  setMaximumDisplayPointCountExceeded,
  setCustomSettingsApplied,
  setFadingSettings,
  setHighlightLastPointSettings,
  setMapTrackingSettings,
  toggleMapTrackingEnabled,
  setTimelinePlayerMapViewCoordinates,
  toggleDisplayDataOnCurrentPoint,
  setSkipXMeters,
  toggleDisplayablePointData,
  transitionToPlayingState,
  transitionToPreviousPlayingState,
  updateTimelinePlayerTimelines,
} = actions;

export const getTimelinePlayerMapViewId = (state: RootStateOr<TimelinePlayerState>) => slice(state).mapViewId;
export const getTimelinePlayerMapViewCoordinatesWithLastUpdate = (state: RootStateOr<TimelinePlayerState>) => ({
  mapViewCoordinates: slice(state).mapViewCoordinates,
  lastMapViewCoordinatesUpdate: slice(state).lastMapViewCoordinatesUpdate,
});
export const isTimelinePlayerMapView = (state: RootStateOr<TimelinePlayerState>, viewId: ViewId) =>
  slice(state).mapViewId === viewId;
export const isTimelinePlayerOpen = (state: RootStateOr<TimelinePlayerState>) => slice(state).open;
export const isTimelinePlayerOpenForView = (state: RootStateOr<TimelinePlayerState>, viewId: ViewId) =>
  isTimelinePlayerOpen(state) && getTimelinePlayerMapViewId(state) === viewId;
export const getTimelinePlayerTime = (state: RootStateOr<TimelinePlayerState>) => slice(state).time;
export const getTimelines = (state: RootStateOr<TimelinePlayerState>) => slice(state).timelines;
export const isUpdatingTimelines = (state: RootStateOr<TimelinePlayerState>) => slice(state).updatingTimelines;
export const isMaxDisplayablePointsExceeded = (state: RootStateOr<TimelinePlayerState>) =>
  slice(state).maximumDisplayPointCountExceeded;
export const getCustomTimelinePlayerSettings = (state: RootStateOr<TimelinePlayerState>) => slice(state).customSettings;
export const isMapTrackingEnabled = (state: RootStateOr<TimelinePlayerState>) =>
  slice(state).customSettings.tracking.trackingEnabled;
export const getSkipXMeters = (state: RootStateOr<TimelinePlayerState>) => slice(state).customSettings.skipXMeters;
export const getTimelinesIdsWithTrackingEnabled = (state: RootStateOr<TimelinePlayerState>) => {
  return Object.entries(slice(state).customSettings.tracking.trackingEnabledByTimeline)
    .filter(([, enabled]) => enabled)
    .map(([timelineId]) => timelineId);
};
export const isDisplayDataOnCurrentPointEnabled = (state: RootStateOr<TimelinePlayerState>) =>
  slice(state).customSettings.displayData.displayDataOnCurrentPoint;
export const isDisplayablePointDataIncluded = (state: RootStateOr<TimelinePlayerState>, data: DisplayablePointData) =>
  slice(state).customSettings.displayData.includeData.includes(data);
export const getIncludedDisplayablePointData = (state: RootStateOr<TimelinePlayerState>) =>
  slice(state).customSettings.displayData.includeData;

export const getPlayingState = (state: RootStateOr<TimelinePlayerState>) => slice(state).playingState;
export const getCurrentPlayingState = (state: RootStateOr<TimelinePlayerState>) => slice(state).playingState.current;

// Trigger timelines update when a timeline dataview is invalidated, added or removed
let lastTimelineDataViewState: {
  dataViewIdsWithTimelineData: DataViewId[];
  invalidatedTimeLineIds: DataViewId[];
} = { dataViewIdsWithTimelineData: [], invalidatedTimeLineIds: [] };
addMiddleware(
  createObserveMiddleware(
    (state: RootStateOr<WorkbookState>) => {
      const dataViewIdsWithTimelineData = getDataViewIdsWithTimelineData(state).toSorted();
      const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state).toSorted();
      return {
        dataViewIdsWithTimelineData,
        invalidatedTimeLineIds: dataViewIdsWithTimelineData.filter((d) => invalidViewsOrDataViews.includes(d)),
      };
    },
    (timelineDataViewState) => {
      // We can't trigger this callback on changed timeline layer lists and invalidations, without also triggering on
      // de-invalidations: We can't ignore the removal of invalidated data views from the list of invalidations with our
      // store observers (including observing middlewares). So we have to check for changes, manually.
      //
      // This can only be changed by changing to a different mapping of invalidations in the state, where:
      // - The state is not going to be changed when the UI caught up to an invalidation (No valid marking on the same property!)
      // - The state is going to be changed even when the UI hasn't been updated yet
      // @see TechnicalDebt CADENZA-38372

      if (
        // Added or removed timeline data views
        !isEqual(
          timelineDataViewState.dataViewIdsWithTimelineData,
          lastTimelineDataViewState.dataViewIdsWithTimelineData,
        ) ||
        // Some timeline data views became invalidated.
        // We can't detect new invalidations before a data view is being marked valid again, though.
        timelineDataViewState.invalidatedTimeLineIds.some(
          (d) => !lastTimelineDataViewState.invalidatedTimeLineIds.includes(d),
        )
      ) {
        getStore().dispatch(updateTimelinePlayerTimelines());
      }

      lastTimelineDataViewState = timelineDataViewState;
    },
    true,
  ),
);

// Client-side state persistence

observeStore(getStore(), getCustomTimelinePlayerSettings, (settings) => {
  cadenzaSessionStorage.setItem(TIMELINE_SETTINGS_KEY, JSON.stringify(settings));
});

function transitionToPlayingStateInternal(state: TimelinePlayerState, newPlayingState: PlayingState) {
  if (state.playingState.current === newPlayingState) {
    return;
  }
  state.playingState.previous = state.playingState.current;
  state.playingState.current = newPlayingState;
}
